import { Box, Button, TextField, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PhoneIcon from '@material-ui/icons/Phone';
import { PhoneRequestStatus } from '@src/types/contact';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { E164PhoneField } from '../CustomMaterialUI';
import InputHelperText from './InputHelperText';

export interface MobileNumberFieldProps {
  value: string;
  savedValue?: string;
  error?: string;
  disabled?: boolean;
  phoneRequestStatus?: PhoneRequestStatus;
  hasWaUserId?: boolean;
  onChange: (value: string) => void;
  onRequestNumber?: () => void;
}

const MobileNumberField: FC<MobileNumberFieldProps> = ({
  value,
  savedValue,
  error,
  disabled,
  phoneRequestStatus,
  hasWaUserId,
  onChange,
  onRequestNumber,
}) => {
  const classes = useStyles();

  const showRequestButton = !savedValue && !phoneRequestStatus && hasWaUserId;

  if (showRequestButton) {
    return (
      <Box className={classes.wrapper}>
        <TextField
          name="mobileNumber"
          className="InputText"
          variant="outlined"
          label={<FormattedMessage id="ContactForm.mobileNumber" />}
          disabled
          InputProps={{
            endAdornment: (
              <Tooltip
                title={
                  <FormattedMessage id="ContactForm.requestNumberHelper" />
                }
                arrow
                placement="bottom-end"
                classes={{ tooltip: classes.tooltip }}
              >
                <Button
                  className={classes.requestButton}
                  onClick={onRequestNumber}
                  startIcon={<PhoneIcon className={classes.requestIcon} />}
                >
                  <FormattedMessage id="ContactForm.requestNumber" />
                </Button>
              </Tooltip>
            ),
          }}
        />
      </Box>
    );
  }

  if (phoneRequestStatus === PhoneRequestStatus.REQUESTED) {
    return (
      <Box className={classes.wrapper}>
        <TextField
          name="mobileNumber"
          className={`InputText ${classes.requestedField}`}
          label={<FormattedMessage id="ContactForm.mobileNumber" />}
          disabled
          value=""
          InputProps={{
            endAdornment: (
              <Tooltip
                title={<FormattedMessage id="ContactForm.requestSentHelper" />}
                arrow
                placement="bottom-end"
                classes={{ tooltip: classes.tooltip }}
              >
                <Box className={classes.requestedContent}>
                  <AccessTimeIcon className={classes.requestedIcon} />
                  <Typography className={classes.requestedText}>
                    <FormattedMessage id="ContactForm.requestSent" />
                  </Typography>
                </Box>
              </Tooltip>
            ),
          }}
        />
      </Box>
    );
  }

  if (phoneRequestStatus === PhoneRequestStatus.OBTAINED) {
    return (
      <Box className={classes.wrapper}>
        <InputHelperText error={error ?? ''}>
          <E164PhoneField
            name="mobileNumber"
            className={`InputText ${classes.linkedField}`}
            value={value ?? ''}
            maxLength={25}
            disabled
            label={<FormattedMessage id="ContactForm.mobileNumber" />}
            onChange={({ target }: { target: HTMLInputElement }) =>
              onChange(target.value)
            }
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <Tooltip
                  title={
                    <FormattedMessage id="ContactForm.numberLinkedHelper" />
                  }
                  arrow
                  placement="bottom-end"
                  classes={{ tooltip: classes.tooltip }}
                >
                  <Box className={classes.linkedContent}>
                    <Typography className={classes.linkedText}>
                      <FormattedMessage id="ContactForm.numberLinked" />
                    </Typography>
                    <CheckCircleOutlineIcon className={classes.linkedIcon} />
                  </Box>
                </Tooltip>
              ),
            }}
          />
        </InputHelperText>
      </Box>
    );
  }

  // Default: normal editable field (no phoneRequestStatus)
  return (
    <InputHelperText error={error ?? ''}>
      <E164PhoneField
        name="mobileNumber"
        className="InputText"
        value={value ?? ''}
        maxLength={25}
        disabled={disabled}
        label={<FormattedMessage id="ContactForm.mobileNumber" />}
        onChange={({ target }: { target: HTMLInputElement }) =>
          onChange(target.value)
        }
        autoComplete="off"
      />
    </InputHelperText>
  );
};

export default MobileNumberField;

const useStyles = makeStyles(theme => ({
  tooltip: {
    maxWidth: 280,
    fontSize: '0.75rem',
  },
  wrapper: {
    marginBottom: 4,
  },
  requestButton: {
    textTransform: 'none',
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: 11,
    whiteSpace: 'nowrap',
    padding: '2px 4px',
    minWidth: 'auto',
    '& .MuiButton-startIcon': {
      marginRight: 3,
    },
  },
  requestIcon: {
    fontSize: '14px !important',
    color: `${theme.palette.primary.main} !important`,
  },
  helperNone: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 4,
    lineHeight: 1.3,
  },
  requestedField: {
    '& .MuiOutlinedInput-root fieldset': {
      borderColor: '#F59E0B !important',
    },
    '& .MuiOutlinedInput-root.Mui-disabled fieldset': {
      borderColor: '#F59E0B !important',
    },
    '& .MuiInputBase-root fieldset': {
      borderColor: '#F59E0B !important',
    },
  },
  requestedContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    cursor: 'default',
  },
  requestedIcon: {
    fontSize: '14px !important',
    color: '#F59E0B !important',
  },
  requestedText: {
    fontSize: 11,
    fontWeight: 500,
    color: '#F59E0B',
    whiteSpace: 'nowrap',
  },
  linkedField: {
    '& .MuiOutlinedInput-root fieldset': {
      borderColor: '#22C55E !important',
    },
    '& .MuiOutlinedInput-root.Mui-disabled fieldset': {
      borderColor: '#22C55E !important',
    },
    '& .MuiInputBase-root fieldset': {
      borderColor: '#22C55E !important',
    },
  },
  linkedContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    cursor: 'default',
  },
  linkedText: {
    fontSize: 11,
    fontWeight: 500,
    color: '#22C55E',
    whiteSpace: 'nowrap',
  },
  linkedIcon: {
    fontSize: '14px !important',
    color: '#22C55E !important',
  },
}));
